import { useMemo, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'
import { ArrowLeft, Copy, Eye, GitCompare, ScrollText } from 'lucide-react'
import { allFields, diffSchemas, type Answers } from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Empty,
  FormRenderer,
  Modal,
  PageHeader,
  Select,
  StatusBadge,
  Table,
  Tabs,
  useAuth,
  useCan,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import {
  FORMS_KEY,
  type FormDetail,
  type VersionSummary,
  type VersionWithSchema,
} from '../../lib/form-types'
import { FormBuilder } from './FormBuilder'

/** Fiche d'un formulaire : constructeur (brouillon), versions, utilisation. */
export function FormPage() {
  const { id = '' } = useParams()
  const { session } = useAuth()
  const canEdit = useCan('forms.update')
  const [tab, setTab] = useState('builder')
  const detail = useQuery({
    queryKey: [...FORMS_KEY, id],
    queryFn: () => session.api.get<FormDetail>(`/admin/forms/${id}`),
  })
  const draftId = detail.data?.versions.find((v) => v.status === 'draft')?.id
  const latest = detail.data?.versions.find((v) => v.status !== 'draft')
  const [draft, published] = useQueries({
    queries: [draftId, latest?.id].map((versionId) => ({
      queryKey: [...FORMS_KEY, id, 'version', versionId],
      queryFn: () => session.api.get<VersionWithSchema>(`/admin/forms/${id}/versions/${versionId}`),
      enabled: Boolean(versionId),
      staleTime: Infinity,
    })),
  })
  const publishedSchema = published?.data?.schema
  const publishedKeys = useMemo(
    () => new Set(publishedSchema ? allFields(publishedSchema).map((f) => f.key) : []),
    [publishedSchema]
  )

  if (detail.isError)
    return (
      <>
        <Back />
        <Alert tone="danger">{errorMessage(detail.error)}</Alert>
      </>
    )
  const form = detail.data
  if (!form) return <Back />

  return (
    <>
      <Back />
      <PageHeader
        eyebrow={<span className="mono">{form.code}</span>}
        title={form.title}
        description={form.description ?? undefined}
        actions={
          <span className="row" style={{ gap: 'var(--space-2)' }}>
            {latest ? (
              <Badge variant="success">Publiée : v{latest.version}</Badge>
            ) : (
              <Badge variant="neutral">Jamais publié</Badge>
            )}
            <Can perm="audit.view">
              <Link
                className="btn btn-ghost btn-sm"
                to={`/audit?ressource=form&id=${encodeURIComponent(form.id)}`}
              >
                <ScrollText aria-hidden="true" /> Journal
              </Link>
            </Can>
          </span>
        }
      />
      <Tabs
        label="Formulaire"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'builder', label: 'Constructeur' },
          {
            value: 'versions',
            label: `Versions (${form.versions.filter((v) => v.status !== 'draft').length})`,
          },
          { value: 'usage', label: `Utilisation (${form.usage.length})` },
        ]}
      />
      <div style={{ marginBlockStart: 'var(--space-5)' }}>
        {tab === 'builder' &&
          (draft?.data ? (
            <FormBuilder
              key={draft.data.id + (draft.data.updatedAt ?? '')}
              formId={form.id}
              draft={draft.data}
              publishedKeys={publishedKeys}
              canEdit={canEdit}
            />
          ) : draftId ? null : (
            <NoDraft form={form} latest={latest} onDone={() => setTab('builder')} />
          ))}
        {tab === 'versions' && <Versions form={form} />}
        {tab === 'usage' && <Usage form={form} />}
      </div>
    </>
  )
}

function Back() {
  return (
    <Link
      to="/formulaires"
      className="btn btn-ghost btn-sm"
      style={{ marginBlockEnd: 'var(--space-3)' }}
    >
      <ArrowLeft aria-hidden="true" /> Tous les formulaires
    </Link>
  )
}

function useDraftFrom(formId: string) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (versionId: string) =>
      session.api.post(`/admin/forms/${formId}/draft`, { versionId }),
    onSuccess: async () => {
      toast.success('Brouillon créé', 'Les versions publiées restent inchangées.')
      await queryClient.invalidateQueries({ queryKey: [...FORMS_KEY, formId] })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
}

function NoDraft({
  form,
  latest,
  onDone,
}: {
  form: FormDetail
  latest?: VersionSummary
  onDone: () => void
}) {
  const draftFrom = useDraftFrom(form.id)
  return (
    <Card>
      <Empty
        title="Aucun brouillon en cours"
        description={
          latest
            ? `La version ${latest.version} est publiée et immuable. Pour modifier le formulaire, créez un brouillon à partir d’elle.`
            : undefined
        }
        action={
          latest && (
            <Can perm="forms.update">
              <Button
                variant="primary"
                icon={<Copy />}
                loading={draftFrom.isPending}
                onClick={() => draftFrom.mutate(latest.id, { onSuccess: onDone })}
              >
                Nouveau brouillon depuis la v{latest.version}
              </Button>
            </Can>
          )
        }
      />
    </Card>
  )
}

function Versions({ form }: { form: FormDetail }) {
  const { session } = useAuth()
  const [viewing, setViewing] = useState<VersionSummary | null>(null)
  const published = form.versions.filter((v) => v.status !== 'draft')
  const [a, setA] = useState(published[1]?.id ?? '')
  const [b, setB] = useState(published[0]?.id ?? '')
  const hasDraft = form.versions.some((v) => v.status === 'draft')
  const draftFrom = useDraftFrom(form.id)
  const [left, right] = useQueries({
    queries: [a, b].map((versionId) => ({
      queryKey: [...FORMS_KEY, form.id, 'version', versionId],
      queryFn: () =>
        session.api.get<VersionWithSchema>(`/admin/forms/${form.id}/versions/${versionId}`),
      enabled: Boolean(versionId),
      staleTime: Infinity,
    })),
  })
  const diff = left?.data && right?.data ? diffSchemas(left.data.schema, right.data.schema) : null

  const columns: Column<VersionSummary>[] = [
    {
      key: 'version',
      header: 'Version',
      cell: (v) =>
        v.status === 'draft' ? <Badge variant="warning">Brouillon</Badge> : `v${v.version}`,
    },
    { key: 'fields', header: 'Champs', numeric: true, cell: (v) => v.fields },
    { key: 'date', header: 'Publiée le', cell: (v) => when(v.publishedAt) },
    { key: 'by', header: 'Par', cell: (v) => v.publishedBy ?? '—' },
    {
      key: 'reason',
      header: 'Objet',
      cell: (v) => <span style={{ fontSize: 'var(--text-sm)' }}>{v.publishReason ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      cell: (v) => (
        <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
          <Button variant="ghost" size="sm" icon={<Eye />} onClick={() => setViewing(v)}>
            Voir
          </Button>
          {v.status !== 'draft' && !hasDraft && (
            <Can perm="forms.update">
              <Button
                variant="ghost"
                size="sm"
                icon={<Copy />}
                onClick={() => draftFrom.mutate(v.id)}
              >
                Brouillon depuis
              </Button>
            </Can>
          )}
        </span>
      ),
    },
  ]

  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      <Card>
        <Table
          columns={columns}
          rows={form.versions}
          rowKey={(v) => v.id}
          caption="Versions"
          empty={<Empty title="Aucune version" />}
        />
      </Card>
      {form.versions.length > 1 && (
        <Card>
          <CardHeader title="Comparer deux versions" />
          <CardBody>
            <div
              className="row"
              style={{ gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}
            >
              {(
                [
                  ['De', a, setA],
                  ['À', b, setB],
                ] as const
              ).map(([label, value, set]) => (
                <Select
                  key={label}
                  label={label}
                  value={value}
                  placeholder="— Version —"
                  options={form.versions.map((v) => ({
                    value: v.id,
                    label: v.status === 'draft' ? 'Brouillon' : `v${v.version}`,
                  }))}
                  onChange={(e) => set(e.target.value)}
                />
              ))}
              <GitCompare aria-hidden="true" />
            </div>
            {diff && (
              <div style={{ marginBlockStart: 'var(--space-4)' }}>
                {diff.length === 0 ? (
                  <p className="text-muted">Aucune différence.</p>
                ) : (
                  <ul className="diff-list">
                    {diff.map((d) => (
                      <li key={d.key}>
                        <Badge
                          variant={
                            d.change === 'added'
                              ? 'success'
                              : d.change === 'removed'
                                ? 'danger'
                                : 'warning'
                          }
                        >
                          {d.change === 'added'
                            ? 'Ajouté'
                            : d.change === 'removed'
                              ? 'Retiré'
                              : 'Modifié'}
                        </Badge>{' '}
                        <strong>{d.label}</strong>{' '}
                        <span className="perm-code" style={{ display: 'inline' }}>
                          {d.key}
                        </span>
                        {d.details.length > 0 && (
                          <span className="text-muted"> — {d.details.join(', ')}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      )}
      {viewing && (
        <VersionPreview formId={form.id} version={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  )
}

function VersionPreview({
  formId,
  version,
  onClose,
}: {
  formId: string
  version: VersionSummary
  onClose: () => void
}) {
  const { session } = useAuth()
  const [answers, setAnswers] = useState<Answers>({})
  const full = useQuery({
    queryKey: [...FORMS_KEY, formId, 'version', version.id],
    queryFn: () =>
      session.api.get<VersionWithSchema>(`/admin/forms/${formId}/versions/${version.id}`),
    staleTime: Infinity,
  })
  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={version.status === 'draft' ? 'Brouillon' : `Version ${version.version}`}
      description="Aperçu tel que le verra le candidat."
    >
      {full.data && (
        <FormRenderer
          schema={full.data.schema}
          value={answers}
          onChange={setAnswers}
          layout="single"
          completeLabel="Vérifier"
        />
      )}
    </Modal>
  )
}

function Usage({ form }: { form: FormDetail }) {
  if (!form.usage.length)
    return (
      <Card>
        <Empty
          title="Aucun concours n’utilise ce formulaire"
          description="Associez une version publiée depuis la fiche d’un concours (onglet « Formulaire »)."
        />
      </Card>
    )
  return (
    <Card>
      <ul className="diff-list" style={{ padding: 'var(--space-4)' }}>
        {form.usage.map((u) => (
          <li key={u.competitionId}>
            <Link to={`/concours/${u.competitionId}`}>{u.title}</Link>{' '}
            <span className="perm-code" style={{ display: 'inline' }}>
              {u.reference}
            </span>{' '}
            <StatusBadge domain="competition" status={u.status as never} />{' '}
            <Badge variant="outline">v{u.version}</Badge>
          </li>
        ))}
      </ul>
    </Card>
  )
}
